/**
 * Lettura quotidiana delle notizie (F-185), nella pagina Notizie: interruttore,
 * orario, «Esegui ora», cosa potrebbe fare oggi ed esito dell'ultima lettura —
 * la proposta consegnata al motore con il suo stato vivo, i titoli messi in
 * wishlist e le notizie che lo studio ha giudicato rilevanti.
 *
 * L'interruttore salva da solo, come nel ripescaggio (F-184): un interruttore
 * che non vale finché non si preme un altro pulsante è il modo più rapido per
 * credere acceso ciò che è spento. Ora e fuso si salvano con «Salva orario».
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Card, Field, Spinner, Switch } from '@/ui';
import { DomainBadge } from '@/ui/DomainBadge';
import {
  isInFlight,
  useNewsReviewOverview,
  useRunNewsReviewNow,
  useUpdateNewsReviewSettings,
  type NewsReviewOverview,
  type NewsReviewPick,
  type NewsReviewProposal,
  type NewsReviewRun,
} from './news-review-api';

type Tone = 'neutral' | 'positive' | 'negative' | 'accent' | 'warning';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

const RUN_STATUS: Record<NewsReviewRun['status'], { label: string; tone: Tone }> = {
  queued: { label: 'in coda', tone: 'warning' },
  running: { label: 'in corso', tone: 'warning' },
  completed: { label: 'completata', tone: 'positive' },
  failed: { label: 'fallita', tone: 'negative' },
};

/** Lo stato della proposta NEL MOTORE: consegnata non vuol dire eseguita. */
const PROPOSAL_STATUS: Record<string, { label: string; tone: Tone }> = {
  deferred: { label: 'in valutazione dal motore', tone: 'warning' },
  proposed: { label: 'in valutazione dal motore', tone: 'warning' },
  approved: { label: 'approvata', tone: 'accent' },
  executed: { label: 'eseguita', tone: 'positive' },
  dry_run: { label: 'simulata, non inviata', tone: 'neutral' },
  rejected: { label: 'respinta dal motore', tone: 'negative' },
  failed: { label: 'fallita', tone: 'negative' },
};

const MODE_LABEL: Record<string, string> = { execute: 'esegue', dry_run: 'simulazione' };

function ProposalRow({ proposal }: { proposal: NewsReviewProposal }) {
  const status = PROPOSAL_STATUS[proposal.status] ?? { label: proposal.status, tone: 'neutral' as const };
  return (
    <li className="news-review__item">
      <div className="news-review__item-head">
        <Badge tone={proposal.side === 'buy' ? 'accent' : 'warning'}>{proposal.side === 'buy' ? 'acquisto' : 'vendita'}</Badge>
        <Link to={`/instruments/${proposal.instrumentId}`}>{proposal.symbol ?? proposal.instrumentName}</Link>
        <span className="text-muted">
          su «{proposal.portfolioName}»{proposal.amount ? ` · ${formatCurrency(proposal.amount, proposal.currency)}` : ''}
          {proposal.code ? ` · ${proposal.code}` : ''}
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      {proposal.rejectionDetail && <div className="text-muted">{proposal.rejectionDetail}</div>}
    </li>
  );
}

function PickRow({ pick }: { pick: NewsReviewPick }) {
  const outcome =
    pick.status === 'added'
      ? `in wishlist su «${pick.portfolioName}» fino al ${formatDate(pick.expiresAt)}`
      : pick.status === 'skipped'
        ? `scelto ma non messo in wishlist: ${pick.note ?? 'motivo non registrato'}`
        : `scelta scartata: ${pick.note ?? 'motivo non registrato'}`;
  return (
    <li className="news-review__item" data-status={pick.status}>
      <div className="news-review__item-head">
        <span className="wishlist__name">{pick.name}</span>
        {pick.symbol && pick.symbol !== pick.name && <span className="text-muted">{pick.symbol}</span>}
        {pick.domain && <DomainBadge domain={pick.domain} />}
        <Badge tone={pick.status === 'added' ? 'positive' : 'neutral'}>confidenza {pick.confidence}/100</Badge>
      </div>
      <div>{outcome}</div>
      <div className="text-muted">{pick.reason}</div>
    </li>
  );
}

function LatestRun({ run }: { run: NewsReviewRun }) {
  const status = RUN_STATUS[run.status];
  return (
    <div className="news-review__run">
      <div className="news-review__item-head">
        <strong>Ultima lettura</strong>
        <Badge tone={status.tone}>{status.label}</Badge>
        <span className="text-muted">
          {run.trigger === 'scheduled' ? 'quotidiana' : 'a mano'} · {formatDateTime(run.startedAt ?? run.createdAt)} · {run.newsCount} notizie lette
        </span>
        {run.agentRunId && <Link to={`/agent-runs/${run.agentRunId}`}>Apri lo studio</Link>}
      </div>
      {isInFlight(run) && <Spinner label="Lo studio sta leggendo le notizie…" />}
      {run.status === 'failed' && (
        <Alert variant="error" title="Lettura non riuscita">
          {run.errorMessage}
        </Alert>
      )}
      {run.summary && <p>{run.summary}</p>}
      {run.emptyReason && <p className="text-muted">{run.emptyReason}</p>}

      {run.proposals.length > 0 && (
        <section>
          <h4 className="news-review__subtitle">Operazione consegnata al motore</h4>
          <ul className="news-review__list">
            {run.proposals.map((proposal) => (
              <ProposalRow key={proposal.id} proposal={proposal} />
            ))}
          </ul>
        </section>
      )}
      {run.picks.length > 0 && (
        <section>
          <h4 className="news-review__subtitle">Wishlist</h4>
          <ul className="news-review__list">
            {run.picks.map((pick) => (
              <PickRow key={`${pick.instrumentId}-${pick.status}`} pick={pick} />
            ))}
          </ul>
        </section>
      )}
      {run.relevantNews.length > 0 && (
        <section>
          <h4 className="news-review__subtitle">Notizie che contano</h4>
          <ul className="news-review__list">
            {run.relevantNews.map((item) => (
              <li key={item.newsItemId} className="news-review__item">
                {item.url ? (
                  <a href={item.url} target="_blank" rel="noreferrer" className="news-page__title">
                    {item.title}
                  </a>
                ) : (
                  <span>{item.title}</span>
                )}
                {item.note && <span className="text-muted">{item.note}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Today({ overview }: { overview: NewsReviewOverview }) {
  const { today } = overview;
  return (
    <ul className="news-review__list news-review__today">
      <li>
        Notizie delle ultime {overview.windowHours} ore: <strong>{today.newsCount}</strong>.
      </li>
      <li>
        {today.operationsDisabledReason ? (
          <span className="text-muted">Oggi non può operare: {today.operationsDisabledReason}.</span>
        ) : (
          <>
            Può consegnare al motore al massimo {overview.maxProposals} operazione: acquisto di uno dei {today.buyableCount} titoli già in
            wishlist, o vendita di una delle {today.sellableCount} posizioni aperte, su{' '}
            {today.operablePortfolios.map((p) => `«${p.name}» (${MODE_LABEL[p.mode] ?? p.mode})`).join(', ')}.
          </>
        )}
      </li>
      <li>
        Fino a {overview.maxWishlistPicks} titoli nuovi in wishlist per {overview.defaultExpiryDays} giorni:{' '}
        {(['stock', 'crypto'] as const).map((domain, index) => {
          const target = today.targets[domain];
          return (
            <span key={domain}>
              {index > 0 && ' · '}
              <DomainBadge domain={domain} />{' '}
              {target ? (
                <>→ «{target.portfolioName}»</>
              ) : (
                <span className="text-muted">
                  nessun portafoglio nel <Link to="/discovery/daily-pick">titolo del giorno</Link>
                </span>
              )}
            </span>
          );
        })}
      </li>
    </ul>
  );
}

function ScheduleForm({ overview }: { overview: NewsReviewOverview }) {
  const save = useUpdateNewsReviewSettings();
  const [form, setForm] = useState(overview.settings);
  // Le impostazioni tornano dal server dopo il salvataggio: il form le rilegge.
  useEffect(() => setForm(overview.settings), [overview.settings]);

  return (
    <div className="news-review__schedule">
      <Field label="Ora" type="time" value={form.runAtLocalTime} onChange={(event) => setForm({ ...form, runAtLocalTime: event.target.value })} />
      <Field
        label="Fuso orario"
        value={form.timezone}
        onChange={(event) => setForm({ ...form, timezone: event.target.value })}
        hint="Nome IANA, es. Europe/Rome."
      />
      <Button
        variant="secondary"
        onClick={() => save.mutate({ runAtLocalTime: form.runAtLocalTime, timezone: form.timezone })}
        disabled={save.isPending}
      >
        {save.isPending ? 'Salvo…' : 'Salva orario'}
      </Button>
      {save.isError && (
        <Alert variant="error" title="Non ho potuto salvare">
          {errorMessage(save.error)}
        </Alert>
      )}
    </div>
  );
}

export function NewsReviewCard() {
  const overview = useNewsReviewOverview();
  const toggle = useUpdateNewsReviewSettings();
  const runNow = useRunNewsReviewNow();

  if (overview.isLoading) return <Spinner label="Caricamento della lettura delle notizie…" />;
  if (overview.isError || !overview.data) {
    return (
      <Alert variant="error" title="Lettura delle notizie non disponibile">
        {errorMessage(overview.error)}
      </Alert>
    );
  }

  const data = overview.data;
  const latest = data.runs[0];
  const busy = isInFlight(latest) || runNow.isPending;

  return (
    <Card
      title="Lettura quotidiana delle notizie"
      subtitle={`Una volta al giorno uno studio agentico legge le notizie delle ultime ${data.windowHours} ore, apre e verifica quelle che contano e, solo se un'analisi lo sostiene, consegna al motore un'operazione o mette titoli in wishlist. Il motore ricontrolla ogni proposta.`}
      action={
        <Button variant="secondary" onClick={() => runNow.mutate()} disabled={!data.agentRuntimeEnabled || busy}>
          {busy ? 'In corso…' : 'Esegui ora'}
        </Button>
      }
    >
      <div className="news-review">
        {!data.agentRuntimeEnabled && (
          <Alert variant="warning" title="Runtime agentico spento sul server">
            La lettura delle notizie non può partire finché il runtime agentico non è attivo.
          </Alert>
        )}
        {(toggle.isError || runNow.isError) && (
          <Alert variant="error" title="Operazione non riuscita">
            {errorMessage(toggle.error ?? runNow.error)}
          </Alert>
        )}

        <Switch
          label="Leggi le notizie ogni giorno"
          checked={data.settings.enabled}
          onChange={(enabled) => toggle.mutate({ enabled })}
          disabled={toggle.isPending}
        />
        <p className="text-muted">
          {data.settings.enabled && data.nextRunAt
            ? `Prossima lettura: ${formatDateTime(data.nextRunAt)}.`
            : 'Spenta: parte solo con «Esegui ora».'}{' '}
          Lo stato di ogni esecuzione compare anche in <Link to="/automations">Automazioni</Link>.
        </p>

        <ScheduleForm overview={data} />
        <Today overview={data} />

        {latest && <LatestRun run={latest} />}
      </div>
    </Card>
  );
}
